import React, { useEffect, useState } from 'react';
import './ChoosePaymentMethod.scss';
import { useSearchParams } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import Button from '../../components/Button';
import { GET_FLEX_CHECKOUT_SESSION, GET_MEMBERSHIP } from '../../services/ApolloClient';
import { Loader } from '../../components/AppLoader';
import { ErrorModal } from '../../components/ErrorModal';
import { paymentMethodEvent } from '../../services/DataPipeline/DataPipeline';
import { PAYMENT_METHOD } from '../../services/DataPipeline/models';
import { ReactComponent as FlexIcon } from '../../assets/images/flexIcon.svg';
import { EMembershipStatus } from '../MyAccount/components/types';
import { navigateWithReload } from '../../utils';

function ChoosePaymentMethod(): React.JSX.Element {
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [flexCheckoutSession] = useLazyQuery(GET_FLEX_CHECKOUT_SESSION);
  const [searchParams] = useSearchParams();
  const checkoutUrl = searchParams.get('checkoutUrl');
  const flLevel = searchParams.get('flLevel');
  const [getMembership, { loading: membershipDataLoading, error: membershipDataError }] =
    useLazyQuery(GET_MEMBERSHIP, {
      fetchPolicy: 'no-cache',
    });

  useEffect(() => {
    if (membershipDataError) {
      setIsLoading(false);
      setIsError(!!membershipDataError);
    }
  }, [membershipDataError]);

  const checkForExistingMembership = async (): Promise<boolean> => {
    const membershipData = await getMembership({
      variables: {
        forceUpdate: true,
      },
    });
    if (membershipData.error) {
      throw membershipData.error;
    }
    if (
      membershipData?.data?.membership &&
      membershipData?.data?.membership?.status !== EMembershipStatus.expired
    ) {
      return true;
    }
    return false;
  };

  const redirect = () => {
    if (checkoutUrl) {
      window.location.href = checkoutUrl;
    }
  };

  const flexHandler = async () => {
    setIsLoading(true);
    try {
      const hasActiveMembership = await checkForExistingMembership();
      if (hasActiveMembership) {
        navigateWithReload('/app/your-path/');
        return;
      }
      const queryParams = Object.fromEntries(searchParams.entries());
      const checkoutRes = await flexCheckoutSession({
        variables: { priceId: flLevel, queryParams },
      });
      if (checkoutRes?.error) {
        setIsError(true);
        return;
      }
      if (checkoutRes.data?.flexCheckoutSession?.redirectUrl) {
        window.location.href = checkoutRes.data?.flexCheckoutSession?.redirectUrl;
      }
    } catch {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const chargeBeeHandler = async () => {
    setIsLoading(true);
    try {
      const hasActiveMembership = await checkForExistingMembership();
      if (hasActiveMembership) {
        navigateWithReload('/app/your-path/');
        return;
      }
      redirect();
    } catch {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="choose-payment-method-container">
        <h2 className="title">Select Payment Method</h2>
        <div className="buttons">
          <Button
            onClick={() => {
              paymentMethodEvent({ payment_method: PAYMENT_METHOD.CHARGEBEE });
              chargeBeeHandler();
            }}
            className="button-item payment-method-selection-credit-debit"
            label="Pay with your credit/debit card"
            styleKey="dark"
            disabled={!checkoutUrl}
          />
          <div className="division">
            <hr className="h-line" />
            <div>or</div>
            <hr className="h-line" />
          </div>
          <Button
            onClick={() => {
              paymentMethodEvent({ payment_method: PAYMENT_METHOD.FLEX });
              flexHandler();
            }}
            className="button-item payment-method-selection-hsa-fsa"
            label="Pay with your HSA/FSA"
            styleKey="light"
            icon={
              <>
                <FlexIcon />
                <div className="v-line" />
              </>
            }
          />
        </div>
        <div className="flex-disclaimer">
          * Please note that promo codes and upgrades are not <br /> supported when purchasing via
          HSA/FSA.
        </div>
      </div>
      {(isLoading || membershipDataLoading) && <Loader />}
      <ErrorModal buttonText="Close" isOpen={isError} onSubmit={() => setIsError(false)} />
    </>
  );
}

export default ChoosePaymentMethod;
